import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_H =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-12/prod_agent_ead185b1-37d3-41b0-8463-37e266b01c59/Logo_Horizontal_Transparent_eeb37a07dc644f8290d0f96bd0f092d9.png";

/* @section: footer */
export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  const services = [
    { en: "Individual Tax Return", ko: "개인 세금보고", to: "/individual-tax" },
    { en: "Small Business Tax Planning", ko: "스몰비즈니스 절세 플랜", to: "/tax-planning" },
    { en: "Business Formation", ko: "회사 설립 지원", to: "/business-formation" },
    { en: "IRS Notice Support", ko: "IRS Letter 대응", to: "/irs-notices" },
    { en: "All Services", ko: "전체 서비스 보기", to: "/services" },
  ];

  return (
    <footer className="bg-primary text-primary-foreground mt-auto">
      <div className="container mx-auto px-4 max-w-7xl py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_H}
              alt="NexBiz Tax Advisor"
              className="h-16 w-auto object-contain mb-4"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <p className="text-sm text-primary-foreground/70 leading-relaxed mb-4">
              {t(
                "Enrolled Agent tax advisory serving small business owners and individuals in San Jose & the Bay Area.",
                "샌호세 및 베이 지역 스몰비즈니스와 개인을 위한 Enrolled Agent 세무 서비스"
              )}
            </p>
            {/* Trust Badges */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-xs text-accent font-semibold">
                <Shield className="w-3.5 h-3.5" />
                {t("Enrolled Agent · IRS Authorized", "Enrolled Agent · IRS 공인")}
              </div>
              <div className="flex items-center gap-2 text-xs text-accent font-semibold">
                <span>🇺🇸 🇰🇷</span>
                {t("Bilingual: English & Korean", "영어 · 한국어 이중언어 서비스")}
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-accent mb-4">
              {t("Services", "서비스")}
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    className="text-sm text-primary-foreground/75 hover:text-accent transition-colors"
                  >
                    {t(s.en, s.ko)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-accent mb-4">
              {t("Quick Links", "바로가기")}
            </h4>
            <ul className="space-y-2.5">
              {[
                { en: "About NexBiz", ko: "NexBiz 소개", to: "/about" },
                { en: "Tax Tips & Resources", ko: "세금 팁 & 자료", to: "/resources" },
                { en: "Contact Us", ko: "문의하기", to: "/contact" },
                { en: "Schedule Consultation", ko: "상담 예약하기", to: "/contact" },
              ].map((l) => (
                <li key={l.to + l.en}>
                  <Link
                    to={l.to}
                    className="text-sm text-primary-foreground/75 hover:text-accent transition-colors"
                  >
                    {t(l.en, l.ko)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-sm font-bold uppercase tracking-wider text-accent mb-4">
              {t("Contact", "연락처")}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-primary-foreground/75">
                <MapPin className="w-4 h-4 mt-0.5 text-accent shrink-0" />
                <span>San Jose, California<br />Bay Area, CA</span>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-primary-foreground/75">
                <Mail className="w-4 h-4 text-accent shrink-0" />
                <a href="mailto:nexbizmanagement@gmail.com" className="hover:text-accent transition-colors">
                  nexbizmanagement@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-primary-foreground/75">
                <Phone className="w-4 h-4 text-accent shrink-0" />
                <a href="tel:+16503099319" className="hover:text-accent transition-colors">
                  (650) 309-9319
                </a>
              </li>
            </ul>
            <Link
              to="/contact"
              className="inline-flex items-center mt-5 px-4 py-2 rounded-md text-sm font-semibold border border-accent text-accent hover:bg-accent hover:text-white transition-colors"
            >
              {t("Schedule Consultation", "상담 예약하기")}
            </Link>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-8 border-t border-primary-foreground/15">
          <div className="bg-primary-foreground/5 rounded-lg p-4 mb-6">
            <p className="text-xs text-primary-foreground/55 leading-relaxed">
              <span className="font-semibold text-primary-foreground/70">
                {t("Disclaimer: ", "면책 고지: ")}
              </span>
              {t(
                "Information on this website is for general educational purposes only and does not constitute tax, legal, or financial advice. Every tax situation is unique. Engagement and advice begin only after a signed engagement agreement. NexBiz Tax Advisor is not a law firm and does not provide legal advice.",
                "이 웹사이트의 내용은 일반적인 교육 목적의 정보이며, 세금·법률·재무 조언을 구성하지 않습니다. 모든 세금 상황은 개별적으로 다릅니다. 공식 서비스 및 조언은 계약서 서명 이후에만 시작됩니다. NexBiz Tax Advisor는 법률 사무소가 아니며 법적 조언을 제공하지 않습니다."
              )}
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/45">
            <p>© {year} NexBiz Tax Advisor. {t("All rights reserved.", "모든 권리 보유.")}</p>
            <p>{t("Enrolled Agent · San Jose, CA · English & Korean", "Enrolled Agent · 샌호세, CA · 영어 & 한국어")}</p>
          </div>
          <div className="mt-4 pt-4 border-t border-primary-foreground/10">
            <p className="text-xs text-primary-foreground/30 text-center leading-relaxed">
              Small Business Tax Planning | Tax Preparation | Business Formation | IRS Notice Assistance | Serving San Jose, Santa Clara, Sunnyvale, Cupertino, Mountain View, Campbell, and the Bay Area.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
